import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  MDBAutocomplete,
  MDBCheckbox,
  MDBIcon,
  MDBInput,
  MDBSelect,
} from "mdb-react-ui-kit";
import { getMisc } from "../../../apiclients/streamsapi_bridge";
import { useNavigate } from "react-router";
import PopupLoaderNew from "../../../views/popups/PopupLoaderNew";
import AddExterNum from "../../../views/popups/misc/AddExterNum";
import { useTranslation } from "react-i18next";
import { validatePhoneNumber } from "../../../views/popups/phonenumbers/AddRemoveNumbers";
import { normalizePhoneNumber } from "../../../apiclients/CommonUtils";
import { externalNumberearch } from "../../../apiclients/streamsapi_bridge";
// ─── constants ────────────────────────────────────────────────────────────────

const CREATE_EXTERNAL_LABEL = "Add External Number";
const MAX_DEFAULT_RESULTS = 10;

// HOP type numbers – mirrors RouteOperator OPERATOR_TYPES
const HOP_TYPES = { User: 2, "Hunt Group": 1, "Call Queue": 4, External: 3 };

// Reverse map: hoptype → category label (for pre-population display)
const HOP_TYPE_CATEGORY = { 1: "Hunt Group", 2: "User", 4: "Call Queue" };

// Default field names (same paths as RouteOperator DEFAULT_FIELD_NAMES)
const DEFAULT_FIELD_NAMES = {
  hopType: "hop.hop_type",
  value: "hop.val",
  uniqueId: "hop.uniqueid",
  cidPassFlag: "hop.cid_pass_flag",
  cidPass: "hop.cid_pass",
  name: "hop.name",
  ph_num: "hop.phnumber",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function itemLabel(item) {
  if (!item) return "";
  const ext = item.extensions ? `${item.extensions} : ` : "";
  const cat = item.category ? ` : ${item.category}` : "";
  return `${ext}${item.title}${cat}`;
}

function getNestedError(errors, path) {
  if (!path) return undefined;
  return path
    .split(".")
    .reduce(
      (acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined),
      errors,
    )?.message;
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * HumanRouteOperator
 *
 * Unified-list picker (MDBAutocomplete) for the Human Operator selection in
 * LunaHumanOperator.  All Users / Hunt Groups / Call Queues are displayed in a
 * single searchable list, matching the RouteCalls UX pattern.
 *
 * External Numbers reuse the exact popup + inline-field flow from RouteOperator
 * (AddExterNum modal → setExDetails/setExUpdate → LunaHumanOperator.sendApiRequest).
 */
function HumanRouteOperator({
  operatorsList,
  operatorValue,
  fieldNames = DEFAULT_FIELD_NAMES,
  setExUpdate,
  setExDetails,
  externError,
  setExternError,
  calleridFlag,
  isLoading,
  setisLoading,
  AddExternalNumber,
  setAddExternalNumber,
  cidPassValue,
}) {
  const {
    setValue,
    watch,
    control,
    register,
    formState: { errors },
    clearErrors,
    setError   
  } = useFormContext();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [callerIDList, setCallerIDList] = useState([]);
  const [mainValue, setMainValue] = useState("");
  const [isExternalSelected, setIsExternalSelected] = useState(false);
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [searchError, setSearchError] = useState("");

  const addUserRef = useRef(null);
  const typingTimerRef = useRef(null);
  const displayedOptionsRef = useRef([]);
  const didSearchErrorRef = useRef("");
  // Tracks last *confirmed* selection — updated only on real select or pre-populate,
  // never on search text changes. Used to restore state when popup is cancelled.
  const confirmedStateRef = useRef({ mainValue: "", isExternalSelected: false });
  const toggleShow15 = () => setAddExternalNumber(!AddExternalNumber);

  const handleExternalPopupClose = useCallback(() => {
    setExternError([]);
     clearErrors(fieldNames.ph_num);
     clearErrors(fieldNames.name);
    setMainValue(confirmedStateRef.current.mainValue);
    setIsExternalSelected(confirmedStateRef.current.isExternalSelected);
    setAddExternalNumber(false);
  }, [setAddExternalNumber, setExternError, clearErrors, fieldNames]);

  function handleClick() {
    addUserRef.current.click();
  }


  useEffect(() => {
    displayedOptionsRef.current = displayedOptions;
  }, [displayedOptions]);

  // ── fetch caller-ID list (same as RouteOperator fetchData) ─────────────────
  useEffect(() => {
    async function fetchData() {
      const response = await getMisc(
        JSON.parse(sessionStorage.getItem("admin_session")).siteid,
        { misc: ["callerid_list"] },
      );
      if (!response.json) return;
      const tdata = await response.json();
      if (!tdata) {
        navigate("/");
        return;
      }
      setCallerIDList(tdata?.misc?.callerid_list ?? []);
    }
    fetchData();
  }, []);

  // ── build unified sorted list from operatorsList ───────────────────────────
  const fullList = useMemo(() => {
    if (!operatorsList) return [];
    const users = (operatorsList.user ?? []).map((u) => ({
      ...u,
      category: "User",
    }));
    const groups = (operatorsList.group ?? []).map((g) => ({
      ...g,
      category: "Hunt Group",
    }));
    const acd = (operatorsList.acdgroup ?? []).map((a) => ({
      ...a,
      category: "Call Queue",
    }));
    return [...users, ...groups, ...acd].sort((a, b) =>
      (a?.extensions ?? Infinity) - (b?.extensions ?? Infinity),
    );
  }, [operatorsList]);

  const buildDefaultDisplay = useCallback(() => {
    return [
      {
        title: CREATE_EXTERNAL_LABEL,
        _isExternal: true,
        _label: CREATE_EXTERNAL_LABEL,
      },
    ];
  }, []);

  useEffect(() => {
    setDisplayedOptions(buildDefaultDisplay());
  }, [buildDefaultDisplay]);

  // ── pre-populate from operatorValue (mirrors RouteOperator useEffect) ──────
  useEffect(() => {
    if (!operatorValue || Object.keys(operatorValue).length === 0) return;
        setExternError([]);
    if (operatorValue.hoptype === 3) {
      // External number already saved
      setIsExternalSelected(true);
      setMainValue(CREATE_EXTERNAL_LABEL);
      confirmedStateRef.current = { mainValue: CREATE_EXTERNAL_LABEL, isExternalSelected: true };
    } else {
      // Internal destination – derive label including category from hoptype
      const cat = HOP_TYPE_CATEGORY[operatorValue.hoptype] ?? "";
      const ext = operatorValue.extension
        ? `${operatorValue.extension} : `
        : "";
      const catStr = cat ? ` : ${cat}` : "";
      const label = `${ext}${operatorValue.title ?? ""}${catStr}`;
      setMainValue(label);
      confirmedStateRef.current = { mainValue: label, isExternalSelected: false };
    }
  }, [operatorValue]);

  // ── search ────────────────────────────────────────────────────────────────
  const onSearch = useCallback(
    (raw) => {
      const term = (raw ?? "").toLowerCase().trim();
      if (!term) {
        setSearchError("");
        setDisplayedOptions(buildDefaultDisplay());
        return;
      }
      setSearchError("");
      const matched = fullList
        .filter(
          (item) =>
            item.title?.toLowerCase().includes(term) ||
            item.extensions?.toString().includes(term)
        )
        .slice(0, MAX_DEFAULT_RESULTS)
        .map((item) => ({ ...item, _label: itemLabel(item) }));

      const results = [
        {
          title: CREATE_EXTERNAL_LABEL,
          _isExternal: true,
          _label: CREATE_EXTERNAL_LABEL,
        },
        ...matched,
      ];
      //  ADD THIS BLOCK (DID SEARCH)
      const digits = term.replace(/\D/g, "");
      if (digits.length >= 10 && digits.length <= 11) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
          searchDID(digits, false);
        }, 500);
      }

      setDisplayedOptions(results);
    },
    [fullList, buildDefaultDisplay]
  );

  // ── selection handler ─────────────────────────────────────────────────────
  const onSelect = useCallback(
    (selectedLabel) => {
      if (!selectedLabel) return;

      if (selectedLabel === CREATE_EXTERNAL_LABEL) {
        clearErrors(fieldNames.hopType);
        // Mirrors RouteOperator handleMainTypeClick:
        // only mark dirty if already an external number (hoptype === 3),
        // otherwise just open the popup without touching form dirty state.
        if (!operatorValue || operatorValue.hoptype !== 3) {
          // confirmedStateRef already holds the last real selection — used to restore on cancel
          setIsExternalSelected(true);
          setMainValue(CREATE_EXTERNAL_LABEL);
          toggleShow15();
        } else {
          setIsExternalSelected(true);
          setMainValue(CREATE_EXTERNAL_LABEL);
          setValue(fieldNames.hopType, HOP_TYPES.External, {
            shouldDirty: true,
          });
        }
        return;
      }

      setIsExternalSelected(false);
      setMainValue(selectedLabel);
      confirmedStateRef.current = { mainValue: selectedLabel, isExternalSelected: false };
      clearErrors(fieldNames.hopType);
      setSearchError("");

      let item = fullList.find((i) => itemLabel(i) === selectedLabel);
      //  ADD THIS (DID fallback)
      if (!item) {
        item = displayedOptionsRef.current.find(
          (i) =>
            i._fromDID &&
            (i._label === selectedLabel || itemLabel(i) === selectedLabel)
        );
      }

      if (item) {
        const { id, code } = item;
        const hopType = item.category === "User"
          ? HOP_TYPES.User
          : item.category === "Hunt Group"
            ? HOP_TYPES["Hunt Group"]
            : HOP_TYPES["Call Queue"];
        let rawValue = id ?? code;
        // _fromDID User items don't carry id — look up the real id from fullList
        if (!rawValue && item._fromDID) {
          const match = fullList.find(
            (i) => String(i.extensions) === String(item.extensions) && i.category === item.category
          );
          rawValue = match?.id ?? match?.code;
        }
        const numericValue = rawValue ? Number(rawValue) : "";
        setValue(fieldNames.hopType, hopType, { shouldDirty: true });
        setValue(fieldNames.value, numericValue, { shouldDirty: true });
      }
    },
    [fullList, fieldNames, clearErrors, setValue, operatorValue],
  );

 const searchDID = async (number, isExternalInput = false) => {
  try {
    if (setisLoading) setisLoading(true);

    const siteid = JSON.parse(
      sessionStorage.getItem("admin_session")
    ).siteid;

    const resp = await externalNumberearch(siteid, number);
    if (setisLoading) setisLoading(false);
    const data = await resp.json();

    if (data.status === "success") {
      if (data.routing_obj) {
        const linked = data.routing_obj;

        if (isExternalInput) {
          const linkedMsg = "This is an internal number. Please provide an external number.";
          didSearchErrorRef.current = linkedMsg;
          setExternError([linkedMsg]);
          setValue(fieldNames.ph_num, number, {
            shouldDirty: true,
          });
        } else {
          if (!linked.luna_hop_valid) {
            setSearchError("This is an internal number. Please enter an internal number assigned to User/Hunt Group.");
            return;
          }
          const linkedItem = {
            ...linked,
            title: linked.title ?? number,
            extensions: linked.extensions ?? number,
            category: linked.category ?? "",
            _label: `${linked.extensions ? linked.extensions + " : " : ""}${linked.title ?? number}${linked.category ? " : " + linked.category : ""}`,
            _fromDID: true,
          };
          setDisplayedOptions((prev) => {
            const withoutOld = prev.filter((i) => !i._fromDID);
            return [withoutOld[0], linkedItem, ...withoutOld.slice(1)];
          });
        }
      } else {
        if (isExternalInput) {
          didSearchErrorRef.current = "";
          setExternError([]);
        }
      }
    } else {
      const msgs = data?.message?.errors ?? [];
      if (isExternalInput) {
        if (msgs.length > 0) {
          const errMsg = msgs[0].is_internal_did
            ? "This is an internal number. Please provide an external number."
            : msgs[0].message;
          didSearchErrorRef.current = errMsg;
          setError(fieldNames.ph_num, {
            type: "server",
            message: errMsg,
          });
        } else {
          didSearchErrorRef.current = "";
          clearErrors(fieldNames.ph_num);
        }
      } else {
        if (msgs.length > 0) {
          setSearchError(msgs[0].is_internal_did ? "This is an internal number. Please enter an internal number assigned to User/Hunt Group." : msgs[0].message);
        }
      }
    }

  } catch (err) { 
    if (setisLoading) setisLoading(false);
    console.error("DID search error:", err);
  }
};
  return (
    <>
      {/* ── Main unified autocomplete with validation ── */}
      <Controller
        name={fieldNames.hopType}
        control={control}
        rules={{
          validate: (value) => {
            if (!value || value === 0 || !mainValue) {
              return "Human Operator is required";
            }
            return true;
          },
        }}
        render={() => (
          <>
            <div className="position-relative w-100 routing-input">
              <MDBAutocomplete
                placeholder="Search and Select Human Operator"
                data={displayedOptions}
                value={mainValue}
                title={mainValue}
                displayValue={(item) => item._label ?? itemLabel(item)}
                onChange={(val) => {
                  setMainValue(val);
                  if (val === "") {
                    setIsExternalSelected(false);
                    clearErrors(fieldNames.hopType);
                    setSearchError("");
                    setDisplayedOptions(buildDefaultDisplay());
                  }
                }}
                onSelect={onSelect}
                onSearch={onSearch}
                onClick={(e) => e.target.select()}
                itemContent={(item) => (
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <span>{item._isExternal ? item.title : itemLabel(item)}</span>
                    {item._isExternal &&
                      <MDBIcon fas icon="plus" size="md" color="primary" />
                    }
                  </div>
                )}
              />
              {/* <div className="routing-clear-icon" style={{transform:"translate(-2px, -58%)"}} onClick={() => {
                setMainValue("");
                setIsExternalSelected(false);
                clearErrors(fieldNames.hopType);
                setDisplayedOptions(buildDefaultDisplay());
              }}>
                <MDBIcon fas icon="times" />
              </div> */}
            </div>
            {getNestedError(errors, fieldNames.hopType) && (
              <p className="text-danger">
                {getNestedError(errors, fieldNames.hopType)}
              </p>
            )}
            {searchError && (
              <p className="text-danger">{searchError}</p>
            )}
          </>
        )}
      />

      {/* ── External Number section – reused from RouteOperator ── */}
      {isExternalSelected && (
        <>
          <div className="mt-3">
            <MDBInput
              type="text"
              placeholder="Enter Name"
              {...register(fieldNames.name, {
                required: "Name should not be empty",
              })}
            />
            {getNestedError(errors, fieldNames.name) && (
              <p className="text-danger">
                {getNestedError(errors, fieldNames.name)}
              </p>
            )}
            <MDBInput
              type="text"
              className="mt-3"
              placeholder="Enter External Number"
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              {...register(fieldNames.ph_num, {
                required: "External Number is required",
                validate: (value) => {
                  if (didSearchErrorRef.current) return didSearchErrorRef.current;
                  return validatePhoneNumber(value);
                },
                onChange: (e) => {
                  normalizePhoneNumber(e);
                  const raw = e.target.value;
                  const digits = raw.replace(/\D/g, "");
                  // clear old error when typing
                  if (externError?.length || didSearchErrorRef.current) {
                    didSearchErrorRef.current = "";
                    setExternError([]);
                    clearErrors(fieldNames.ph_num);
                  }
                  //  DID search
                  if (digits.length >= 10 && digits.length <= 11) {
                    clearTimeout(typingTimerRef.current);
                    typingTimerRef.current = setTimeout(() => {
                      searchDID(digits, true);
                    }, 500);
                  }
                }
              })}
            />
            {getNestedError(errors, fieldNames.ph_num) && (
              <p className="text-danger">
                {getNestedError(errors, fieldNames.ph_num)}
              </p>
            )}
            {!getNestedError(errors, fieldNames.ph_num) && externError?.length > 0 && (
              <p className="text-danger">{typeof externError[0] === "string" ? externError[0] : externError[0]?.message}</p>
            )}

            <div className="d-flex align-items-center justify-content-between mt-2">
              <label htmlFor="hopCidPassFlag" className="fw-bold">
                Caller ID Pass Through
              </label>
              <MDBCheckbox
                name="hopCidPassFlag"
                id="hopCidPassFlag"
                checked={watch(fieldNames.cidPassFlag)}
                onChange={(e) => {
                  setValue(fieldNames.cidPassFlag, e.target.checked, {
                    shouldDirty: true,
                  });
                  if (e.target.checked) {
                    clearErrors(fieldNames.cidPass);
                  }
                }}
              />
            </div>

            <Controller
              name={fieldNames.cidPass}
              control={control}
              rules={{
                validate: (value) => {
                  if (watch(fieldNames.cidPassFlag)) return true;
                  if (!value || value === "-1" || value === "") return "Caller ID is required";
                  return validatePhoneNumber(value);
                },
              }}
              render={({ field }) => (
                <>
                  <MDBSelect
                    disabled={watch(fieldNames.cidPassFlag)}
                    name={fieldNames.cidPass}
                    value={
                      watch(fieldNames.cidPassFlag)
                        ? "-1"
                        : watch(fieldNames.cidPass) || "-1"
                    }
                    search
                    onChange={(e) => {
                      if (
                        e &&
                        e.value &&
                        e.value !== cidPassValue &&
                        e.value !== "-1"
                      ) {
                        clearErrors(fieldNames.cidPass);
                        setValue(fieldNames.cidPass, e.value, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    data={[
                      { value: "-1", text: "Choose Caller ID" },
                      ...callerIDList.map((item) => ({
                        value: item.phnum,
                        text: item.addl_info,
                      })),
                    ]}
                  />
                  {getNestedError(errors, fieldNames.cidPass) && (
                    <p className="text-danger">
                      {getNestedError(errors, fieldNames.cidPass)}
                    </p>
                  )}
                </>
              )}
            />
          </div>

          {AddExternalNumber && (
            <PopupLoaderNew
              size="md"
              title={t("addExternal")}
              content={
                <AddExterNum
                  setExDetails={setExDetails}
                  setExUpdate={setExUpdate}
                  onClose={handleExternalPopupClose}
                  externError={externError}
                  setExternError={setExternError}
                  callerids={callerIDList}
                  calleridFlag={calleridFlag}
                  ref={addUserRef}
                  setIsloading={setisLoading}
                  enableDIDSearch={true}
                />
              }
              onShow={setAddExternalNumber}
              onClose={handleExternalPopupClose}
              onSave={handleClick}
              isloading={isLoading}
              flag={1}
            />
          )}
        </>
      )}
    </>
  );
}

export default HumanRouteOperator;
